// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Select,
  TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../utils/api';
import { toCents, billingErrorMessage } from '../../utils/formatFacturation';

const EMPTY_DRAFT = {
  organisation_id: '', site_id: '', period_start: '', period_end: '', service_date: '',
  prix_ht: '', tva_pourcent: '', contract_id: '', description: '', notes: '',
};

/**
 * Création d'un brouillon de facture : établissement, période, prix par chambre
 * et lignes de forfait. Le nombre de chambres facturées est relevé par le
 * serveur, jamais saisi ici.
 */
export default function DraftDialog({ ouvert: isOpen, orgs, regime, onFermer: onClose, onCree }) {
  const franchise = !regime || regime.franchise;
  const [form, setForm] = useState(EMPTY_DRAFT);
  const [rows, setLines] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setForm(EMPTY_DRAFT); setLines([]); setError(''); }
  }, [isOpen]);

  const org = orgs.find((o) => o.id === form.organisation_id);
  const sites = org?.sites || [];
  const priceCents = toCents(form.prix_ht);
  const vatBp = form.tva_pourcent === '' ? null : Math.round(Number(String(form.tva_pourcent).replace(',', '.')) * 100);

  const valid = form.site_id && form.period_start && form.period_end
    && priceCents !== null && priceCents >= 0
    && form.period_end >= form.period_start
    && rows.every((l) => l.description.trim().length >= 2 && toCents(l.prix_ht) !== null);

  const save = async () => {
    setSending(true); setError('');
    try {
      const body = {
        site_id: form.site_id,
        period_start: form.period_start,
        period_end: form.period_end,
        unit_price_ht_cents: priceCents,
      };
      if (form.service_date) body.service_date = form.service_date;
      // Franchise en base : aucune TVA, le taux part à 0 et n'est pas saisi.
      // Assujetti : un taux laissé vide prend le défaut de l'API, jamais une
      // valeur figée dans le navigateur.
      if (franchise) body.vat_rate_bp = 0;
      else if (vatBp !== null && !Number.isNaN(vatBp)) body.vat_rate_bp = vatBp;
      if (form.contract_id) body.contract_id = form.contract_id;
      if (form.description) body.description = form.description;
      if (form.notes) body.notes = form.notes;
      if (rows.length) {
        body.lignes_forfait = rows.map((l) => {
          const ligne = {
            description: l.description.trim(),
            unit_price_ht_cents: toCents(l.prix_ht),
          };
          if (l.quantite !== '') ligne.quantity_hundredths = Math.round(Number(String(l.quantite).replace(',', '.')) * 100);
          if (l.unite) ligne.unit = l.unite;
          if (franchise) ligne.vat_rate_bp = 0;
          else if (l.tva_pourcent !== '') ligne.vat_rate_bp = Math.round(Number(String(l.tva_pourcent).replace(',', '.')) * 100);
          return ligne;
        });
      }
      const r = await api.post('/invoices/brouillons', body);
      onCree(r.data);
    } catch (e) {
      setError(billingErrorMessage(e, 'Le brouillon n\'a pas pu être créé'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Nouveau brouillon de facture</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Alert severity="info" sx={{ mb: 2 }}>
          Le nombre de chambres facturées est relevé par le serveur au moment de
          créer le brouillon, et figé dedans. Le compteur de chambres du
          back office ne compte pas tout à fait les mêmes lignes : le seul
          chiffre juste est celui que le brouillon rendra, avec le relevé nominatif
          des chambres retenues.
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Organisation</InputLabel>
              <Select
                value={form.organisation_id}
                label="Organisation"
                onChange={(e) => setForm((f) => ({ ...f, organisation_id: e.target.value, site_id: '' }))}
              >
                {orgs.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small" disabled={!sites.length}>
              <InputLabel>Établissement facturé</InputLabel>
              <Select
                value={form.site_id}
                label="Établissement facturé"
                onChange={(e) => setForm((f) => ({ ...f, site_id: e.target.value }))}
              >
                {sites.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.city ? ` (${s.city})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <TextField fullWidth size="small" type="date" label="Début de période"
              InputLabelProps={{ shrink: true }} value={form.period_start}
              onChange={(e) => setForm((f) => ({ ...f, period_start: e.target.value }))} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="date" label="Fin de période"
              InputLabelProps={{ shrink: true }} value={form.period_end}
              error={Boolean(form.period_end) && form.period_end < form.period_start}
              helperText={form.period_end && form.period_end < form.period_start ? 'période inversée, refusée par l\'API' : ' '}
              onChange={(e) => setForm((f) => ({ ...f, period_end: e.target.value }))} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="date" label="Date d'exécution du service"
              InputLabelProps={{ shrink: true }} value={form.service_date}
              helperText="Vide : la fin de période fait foi"
              onChange={(e) => setForm((f) => ({ ...f, service_date: e.target.value }))} />
          </Grid>

          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Prix HT par chambre et par mois (€)"
              value={form.prix_ht}
              onChange={(e) => setForm((f) => ({ ...f, prix_ht: e.target.value }))}
              helperText={priceCents === null
                ? 'Le tarif se lit au contrat. Aucun montant n\'est proposé ici.'
                : `Sera envoyé : ${priceCents} centimes hors taxes`} />
          </Grid>
          <Grid item xs={6}>
            {franchise ? (
              <TextField fullWidth size="small" label="TVA" value="0 %" disabled
                helperText={regime?.mention || 'TVA non applicable, art. 293 B du CGI'} />
            ) : (
              <TextField fullWidth size="small" label="Taux de TVA (%)"
                value={form.tva_pourcent}
                onChange={(e) => setForm((f) => ({ ...f, tva_pourcent: e.target.value }))}
                helperText={form.tva_pourcent === ''
                  ? 'Vide : le taux par défaut de l\'API s\'applique'
                  : `Sera envoyé : ${vatBp} points de base`} />
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Objet de la facture" value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Identifiant de contrat (facultatif)"
              value={form.contract_id}
              onChange={(e) => setForm((f) => ({ ...f, contract_id: e.target.value }))}
              helperText="Le rattachement est vérifié par l'API, pas seulement enregistré" />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Observations" value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">Lignes de forfait (installation, matériel, prestation ponctuelle)</Typography>
          <Button size="small" startIcon={<AddIcon />}
            onClick={() => setLines((l) => [...l, { description: '', prix_ht: '', quantite: '', unite: '', tva_pourcent: '' }])}>
            Ajouter une ligne
          </Button>
        </Box>
        {rows.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            Aucune ligne de forfait. La facture ne portera que l&apos;abonnement par chambre.
          </Typography>
        ) : rows.map((l, i) => (
          <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Description" value={l.description}
                onChange={(e) => setLines((xs) => xs.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
            </Grid>
            <Grid item xs={2}>
              <TextField fullWidth size="small" label="Prix HT (€)" value={l.prix_ht}
                onChange={(e) => setLines((xs) => xs.map((x, j) => (j === i ? { ...x, prix_ht: e.target.value } : x)))} />
            </Grid>
            <Grid item xs={2}>
              <TextField fullWidth size="small" label="Quantité" value={l.quantite} placeholder="1"
                onChange={(e) => setLines((xs) => xs.map((x, j) => (j === i ? { ...x, quantite: e.target.value } : x)))} />
            </Grid>
            <Grid item xs={2}>
              <TextField fullWidth size="small" label="Unité" value={l.unite} placeholder="forfait"
                onChange={(e) => setLines((xs) => xs.map((x, j) => (j === i ? { ...x, unite: e.target.value } : x)))} />
            </Grid>
            <Grid item xs={1}>
              {franchise ? (
                <TextField fullWidth size="small" label="TVA %" value="0" disabled />
              ) : (
                <TextField fullWidth size="small" label="TVA %" value={l.tva_pourcent}
                  onChange={(e) => setLines((xs) => xs.map((x, j) => (j === i ? { ...x, tva_pourcent: e.target.value } : x)))} />
              )}
            </Grid>
            <Grid item xs={1}>
              <IconButton size="small" onClick={() => setLines((xs) => xs.filter((x, j) => j !== i))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={!valid || sending} onClick={save}>
          {sending ? <CircularProgress size={18} /> : 'Créer le brouillon'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
